/**
 * Guia → página HTML autónoma (imagens incluídas), com a marca do espaço ou a da Nolens.
 * Todo o texto vem de pessoas e de páginas gravadas: é sempre escapado.
 */
import { PRODUCT } from '../brand';
import {
  DEFAULT_BRAND,
  FONT_STACKS,
  isHexColor,
  readableAccent,
  softOf,
  type GuideBrand,
} from '../brand-kit';
import {
  actionCount,
  displayUrl,
  estimatedMinutes,
  guideSites,
  stepNumbers,
  stepTitle,
  type Guide,
} from '../steps';
import { formatDate } from '@nolens/i18n';
import { guideLocale, guideText } from '../guide-text';
import { cleanUrl } from '../api';

export function esc(s: unknown): string {
  return String(s ?? '').replace(
    /[&<>"']/g,
    (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' })[c]!,
  );
}

/** Só aceitamos imagens e letras embutidas (data:) ou https nos atributos. */
const safeSrc = (u: string | null | undefined) =>
  u && /^(data:(image|font)\/[a-z0-9.+-]+;base64,|https:\/\/)/i.test(u) ? u : null;

export interface HtmlOptions {
  now?: Date;
  brand?: Partial<GuideBrand>;
}

/** images: id do passo → data URL (já com zoom e marcador). */
export function buildHtml(
  guide: Pick<Guide, 'title' | 'description' | 'steps' | 'language'>,
  images: Record<string, string | null>,
  opts: HtmlOptions | Date = {},
): string {
  const o: HtmlOptions = opts instanceof Date ? { now: opts } : opts;
  const now = o.now ?? new Date();
  const brand: GuideBrand = { ...DEFAULT_BRAND, ...o.brand };
  const accentRaw = isHexColor(brand.accent) ? brand.accent : PRODUCT.accent;
  const accent = readableAccent(accentRaw);
  const L = guideLocale(guide);
  const t = guideText(L);
  const title = guide.title.trim() || t.guide;
  const apps = guideSites(guide);
  const numbers = stepNumbers(guide);
  const count = actionCount(guide);
  const logo = safeSrc(brand.logo);
  const fontUrl = brand.font === 'custom' ? safeSrc(brand.fontUrl) : null;
  const font = brand.font === 'custom' && !fontUrl ? FONT_STACKS.inter : FONT_STACKS[brand.font];
  const steps = guide.steps
    .map((s, i) => {
      if (s.kind === 'text') {
        const tone = s.tone || 'info';
        if (tone === 'heading')
          return `<h2 class="section">${esc(s.title.trim() || t.heading)}</h2>`;
        return (
          `<aside class="block ${tone}"><b>${esc(s.title.trim() || t[tone])}</b>` +
          (s.description.trim() ? `<p>${esc(s.description.trim())}</p>` : '') +
          `</aside>`
        );
      }
      const n = numbers.get(s.id) || i + 1;
      const img = safeSrc(images[s.id]);
      const href = cleanUrl(s.url);
      const nav =
        (s.kind === 'navigate' || s.kind === 'tab') && href
          ? `<a class="url" href="${esc(href)}">${esc(displayUrl(s.url))}</a>`
          : '';
      return (
        `<section class="step"><div class="head"><span class="n">${n}</span><h2>${esc(stepTitle(s, L))}</h2></div>` +
        (s.description.trim() ? `<p class="desc">${esc(s.description.trim())}</p>` : '') +
        nav +
        (img ? `<img src="${esc(img)}" alt="${esc(t.step + ' ' + n)}"/>` : '') +
        `</section>`
      );
    })
    .join('\n');
  const header = logo
    ? `<div class="brand"><img class="logo" src="${esc(logo)}" alt=""/>${brand.name ? `<b>${esc(brand.name)}</b>` : ''}</div>`
    : `<div class="brand"><b>Nolens</b> <span>${esc(PRODUCT.name)}</span></div>`;
  return `<!doctype html>
<html lang="${L}"><head><meta charset="utf-8"/><meta name="viewport" content="width=device-width,initial-scale=1"/>
<title>${esc(title)}</title>
<style>
${fontUrl ? `@font-face{font-family:'BrandFont';src:url("${esc(fontUrl)}") format('woff2');font-display:swap}` : ''}
:root{--ink:#141416;--t2:#5c5c63;--t3:#696970;--b:#e7e6e2;--s0:#f7f7f5;--s1:#fff;--accent:${accent};--soft:${softOf(accentRaw)}}
*{box-sizing:border-box}body{margin:0;background:var(--s0);color:var(--ink);font:16px/1.55 ${font}}
main{max-width:820px;margin:0 auto;padding:40px 20px 64px}
.brand{display:flex;align-items:center;gap:10px;font-size:13px;color:var(--t3);margin-bottom:28px}.brand b{color:var(--ink)}.brand span{color:var(--accent)}
.logo{height:28px;width:auto;max-width:180px;border:0;border-radius:0;margin:0}
h1{font-size:32px;line-height:1.2;letter-spacing:-.02em;margin:0 0 10px}
.lead{color:var(--t2);margin:0 0 14px;white-space:pre-wrap}
.meta{font-size:14px;color:var(--t3);margin-bottom:8px}
.apps{display:flex;flex-wrap:wrap;gap:6px;margin-bottom:28px}.apps span{display:inline-flex;align-items:center;gap:6px;font-size:13px;border:1px solid var(--b);background:var(--s1);border-radius:8px;padding:3px 10px}.apps img{width:16px;height:16px;border-radius:3px}
.step{background:var(--s1);border:1px solid var(--b);border-radius:16px;padding:18px 18px 16px;margin:0 0 14px;break-inside:avoid}
.head{display:flex;gap:12px;align-items:flex-start}.n{flex:none;width:30px;height:30px;border-radius:50%;background:var(--soft);color:var(--accent);font-weight:600;font-size:14px;display:inline-flex;align-items:center;justify-content:center}
h2{font-size:17px;font-weight:600;margin:3px 0 0;line-height:1.4}
.desc{margin:8px 0 0 42px;color:var(--t2);white-space:pre-wrap}
.url{display:inline-block;margin:8px 0 0 42px;color:var(--accent);word-break:break-all;font-size:14px}
.step img{display:block;width:100%;margin-top:14px;border-radius:10px;border:1px solid var(--b)}
footer{margin-top:28px;font-size:12.5px;color:var(--t3)}
.section{font-size:22px;letter-spacing:-.01em;margin:30px 0 12px}
.block{border-radius:12px;padding:12px 16px;margin:0 0 14px;break-inside:avoid;background:#f1f1ee;border-left:4px solid #d6d5d0}
.block p{margin:4px 0 0;white-space:pre-wrap}.block.tip{background:#e6f2ea;border-color:#1f7a4d}.block.alert{background:#f8efdf;border-color:#8f5b0a}
@media print{body{background:#fff}main{padding:0}.step{border-color:#ddd}}
</style></head><body><main>
${header}
<h1>${esc(title)}</h1>
${guide.description.trim() ? `<p class="lead">${esc(guide.description.trim())}</p>` : ''}
<div class="meta">${t.steps(count)} · ${estimatedMinutes(guide)} min · ${esc(formatDate(L, now))}</div>
${apps.length ? `<div class="apps">${apps.map((a) => `<span title="${esc(a.host)}">${a.icon ? `<img src="${a.icon}" alt="">` : ''}${esc(a.name)}</span>`).join('')}</div>` : ''}
${steps}
<footer>${esc(brand.footer.trim() || t.madeWith)}</footer>
</main></body></html>`;
}
